import { useEffect } from "react";

/**
 * Publishes the height of the on-screen keyboard as the CSS variable
 * `--keyboard-inset` on <html> (0px when no keyboard is up).
 *
 * Why this is needed: the app shell is a fixed `100dvh` box and <main> is the
 * only scroll container (MainLayout.tsx). On iOS Safari — and on any browser
 * that overlays the keyboard instead of resizing the layout viewport — opening
 * the keyboard does not shrink that box, so whatever sits at the bottom of a
 * short page (e.g. the pagination bar under a narrowed search result list) is
 * hidden behind the keyboard with no scroll space left to bring it back up.
 *
 * Reserving the keyboard's height as extra scrollable space at the end of the
 * scroll container gives the user room to scroll those controls into view.
 * Browsers that genuinely resize the viewport (Chrome on Android) report an
 * inset of ~0 here, so they are unaffected.
 */
const useKeyboardInset = () => {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const root = document.documentElement;

    const update = () => {
      // How much of the layout viewport the visual viewport no longer covers
      // at the bottom. Small values are browser chrome (collapsing URL bar),
      // not a keyboard, so they are ignored.
      const overlap = window.innerHeight - (vv.height + vv.offsetTop);
      const inset = overlap > 80 ? Math.round(overlap) : 0;
      root.style.setProperty("--keyboard-inset", `${inset}px`);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      root.style.removeProperty("--keyboard-inset");
    };
  }, []);
};

export default useKeyboardInset;
